/**
 * Per-user "pinned nav items" memory.
 *
 * 用户把侧栏菜单项 pin 到 My Workspace 的快捷入口集合(≤3),持久化到 localStorage,
 * 按 userId 命名空间隔离(换账号不串)。纯前端,后端无感知。
 *
 * 设计要点:
 *   - 只存 SnowflakeId 数组(有序),镜像项在渲染层按 id 解析回 MenuItem。
 *   - localStorage 读写全 try/catch:隐私模式 / quota / 禁用时降级为本会话内存态。
 *   - userId 为空(未登录态/加载中)时不落盘,但内存态照常工作。
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SnowflakeId } from '@/types/snowflake'

export const PINNED_NAV_KEY_PREFIX = 'sidebar:pinnedNav'
export const MAX_PINNED_NAV = 3

export function buildPinnedNavKey(userId: string): string {
  return `${PINNED_NAV_KEY_PREFIX}:${userId || 'anon'}`
}

const loadPinned = (userId: string): SnowflakeId[] => {
  if (typeof window === 'undefined' || !userId) return []
  try {
    const raw = window.localStorage.getItem(buildPinnedNavKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((x): x is string => typeof x === 'string' && x.length > 0)
      .slice(0, MAX_PINNED_NAV) as SnowflakeId[]
  } catch {
    return []
  }
}

const savePinned = (userId: string, ids: SnowflakeId[]): void => {
  if (typeof window === 'undefined' || !userId) return
  try {
    window.localStorage.setItem(buildPinnedNavKey(userId), JSON.stringify(ids))
  } catch {
    // quota / 隐私模式 / 禁用 —— 非致命,放弃持久化
  }
}

export interface UsePinnedNavResult {
  pinnedIds: SnowflakeId[]
  isPinned: (id: SnowflakeId) => boolean
  canPinMore: boolean
  togglePin: (id: SnowflakeId) => void
}

export function usePinnedNav(userId: string): UsePinnedNavResult {
  const [pinnedIds, setPinnedIds] = useState<SnowflakeId[]>(() => loadPinned(userId))

  // 切换账号(userId 变)时重载该用户的 pin 集合
  useEffect(() => {
    setPinnedIds(loadPinned(userId))
  }, [userId])

  const togglePin = useCallback(
    (id: SnowflakeId) => {
      setPinnedIds((prev) => {
        let next: SnowflakeId[]
        if (prev.includes(id)) {
          next = prev.filter((x) => x !== id)
        } else if (prev.length >= MAX_PINNED_NAV) {
          return prev // 满额,未 pin 项 no-op
        } else {
          next = [...prev, id]
        }
        savePinned(userId, next)
        return next
      })
    },
    [userId],
  )

  const isPinned = useCallback((id: SnowflakeId) => pinnedIds.includes(id), [pinnedIds])

  return useMemo(
    () => ({ pinnedIds, isPinned, canPinMore: pinnedIds.length < MAX_PINNED_NAV, togglePin }),
    [pinnedIds, isPinned, togglePin],
  )
}
